<template>
  <nav class="sticky top-0 z-50 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-800 shadow-sm">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 py-4">
      <!-- Desktop: Horizontal Phase Navigation -->
      <div class="hidden xl:flex items-center gap-2 overflow-x-auto -my-1">
        <template v-for="(phase, index) in phases" :key="phase.key">
          <!-- Phase Button -->
          <button
            @click="navigateToPhase(phase)"
            :disabled="!canNavigateToPhase(phase.key)"
            :class="[
              'flex-shrink-0 group relative flex items-center gap-2 px-3 xl:px-4 py-2 xl:py-2.5 rounded-lg font-medium text-sm transition-all duration-200',
              currentPhase === phase.key
                ? 'bg-blue-600 text-white shadow-md'
                : canNavigateToPhase(phase.key)
                ? 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-sm'
                : 'bg-gray-50 dark:bg-gray-900/50 text-gray-400 dark:text-gray-600 border border-gray-200 dark:border-gray-800 cursor-not-allowed'
            ]"
            :title="getPhaseTooltip(phase)"
          >
            <!-- Step Number Circle -->
            <span 
              :class="[
                'flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold transition-colors',
                currentPhase === phase.key
                  ? 'bg-white text-blue-600'
                  : canNavigateToPhase(phase.key) && isPhaseCompleted(phase.key)
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                  : canNavigateToPhase(phase.key)
                  ? 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 group-hover:bg-blue-100 dark:group-hover:bg-blue-900/30 group-hover:text-blue-600 dark:group-hover:text-blue-400'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600'
              ]"
            >
              <!-- Checkmark for completed phases -->
              <svg 
                v-if="canNavigateToPhase(phase.key) && isPhaseCompleted(phase.key) && currentPhase !== phase.key"
                class="w-3.5 h-3.5" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
              </svg>
              <!-- Lock for locked phases -->
              <svg
                v-else-if="!canNavigateToPhase(phase.key)"
                class="w-3 h-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                />
              </svg>
              <!-- Number for active/available phases -->
              <span v-else>{{ index + 1 }}</span>
            </span>
            
            <!-- Phase Label -->
            <span class="whitespace-nowrap">{{ phase.label }}</span>
          </button>

          <!-- Arrow Separator with Progress Line -->
          <div 
            v-if="index < phases.length - 1"
            class="flex items-center gap-1 shrink-0"
          >
            <div 
              :class="[
                'h-0.5 w-4 xl:w-6 transition-colors duration-300',
                canNavigateToPhase(phases[index + 1].key) || isPhaseCompleted(phase.key)
                  ? 'bg-blue-400 dark:bg-blue-500'
                  : 'bg-gray-300 dark:bg-gray-700'
              ]"
            ></div>
            <svg
              :class="[
                'w-4 h-4 transition-colors duration-300',
                canNavigateToPhase(phases[index + 1].key) || isPhaseCompleted(phase.key)
                  ? 'text-blue-400 dark:text-blue-500'
                  : 'text-gray-300 dark:text-gray-700'
              ]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </template>
      </div>

      <!-- Mobile: Vertical Stacked Layout -->
      <div class="xl:hidden space-y-2">
        <template v-for="(phase, index) in phases" :key="phase.key">
          <button
            @click="navigateToPhase(phase)"
            :disabled="!canNavigateToPhase(phase.key)"
            :class="[
              'w-full px-4 py-3 rounded-lg font-medium text-sm transition-all duration-200 text-left flex items-center justify-between',
              currentPhase === phase.key
                ? 'bg-blue-600 text-white shadow-md ring-2 ring-blue-600 ring-offset-2'
                : canNavigateToPhase(phase.key)
                ? 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600'
                : 'bg-gray-50 dark:bg-gray-900/50 text-gray-400 dark:text-gray-600 border border-gray-200 dark:border-gray-800 cursor-not-allowed'
            ]"
            :title="getPhaseTooltip(phase)"
          >
            <span class="flex items-center gap-2.5">
              <!-- Step Number Circle -->
              <span 
                :class="[
                  'flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold transition-colors',
                  currentPhase === phase.key
                    ? 'bg-white text-blue-600'
                    : canNavigateToPhase(phase.key) && isPhaseCompleted(phase.key)
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                    : canNavigateToPhase(phase.key)
                    ? 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600'
                ]"
              >
                <!-- Checkmark for completed phases -->
                <svg 
                  v-if="canNavigateToPhase(phase.key) && isPhaseCompleted(phase.key) && currentPhase !== phase.key"
                  class="w-3.5 h-3.5" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
                </svg>
                <!-- Lock for locked phases -->
                <svg
                  v-else-if="!canNavigateToPhase(phase.key)"
                  class="w-3 h-3"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
                <!-- Number for active/available phases -->
                <span v-else>{{ index + 1 }}</span>
              </span>
              
              {{ phase.label }}
            </span>
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </template>
      </div>
    </div>
  </nav>
</template>

<script setup>
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useSessionStore } from '@/stores/session'
import { useResearchStore } from '@/stores/research'
import { useDocumentStore } from '@/stores/document'
import { useRiskAnalysisStore } from '@/stores/riskAnalysis'
import { useDataReviewStore } from '@/stores/data-review'

const route = useRoute()
const router = useRouter()
const sessionStore = useSessionStore()
const researchStore = useResearchStore()
const documentStore = useDocumentStore()
const riskAnalysisStore = useRiskAnalysisStore()
const dataReviewStore = useDataReviewStore()

// Phase definitions with routes
const phases = [
  { key: 'organize', label: 'Organize', route: 'Projects' },
  { key: 'find', label: 'Find', route: 'Research' },
  { key: 'research', label: 'Research', route: 'DataCollection' },
  { key: 'analyzeData', label: 'Data', route: 'DataOverview' },
  { key: 'analyzeRisk', label: 'Risk', route: 'Analyze' },
  { key: 'report', label: 'Report', route: 'Report' }
]

// Determine current phase based on route
const currentPhase = computed(() => {
  const path = route.path
  if (path === '/projects' || path === '/') return 'organize'
  if (path.includes('/research') && !path.includes('/data-collection')) return 'find'
  if (path.includes('/data-collection')) return 'research'
  if (path.includes('/data-overview')) return 'analyzeData'
  if (path.includes('/analyze')) return 'analyzeRisk'
  if (path.includes('/report')) return 'report'
  return 'organize'
})

// Get projectId and sessionId from route
const projectId = computed(() => route.query.projectId)
const sessionId = computed(() => route.query.sessionId || sessionStore.session?._id)

// Phase availability logic
const phaseAvailability = computed(() => {
  const session = sessionStore.session
  const hasEntity = session?.entityInfo?.primary != null || session?.disambiguationResults?.topCandidate != null
  
  // Special case: if we're on Research view (Find phase) and no session loaded,
  // show phases based on whether user is viewing session list or has selected a session
  const isOnFindPhase = currentPhase.value === 'find'
  
  if (isOnFindPhase && !sessionId.value) {
    // User is on session list view - show that Research is next but locked
    return {
      organize: true, // Always available
      find: true, // Current phase
      research: false, // Need to select a session first
      analyzeData: false,
      analyzeRisk: false,
      report: false
    }
  }
  
  // Check if we have collection data OR notebook entries
  const hasCollectionData = sessionId.value ? researchStore.getCollectionResults(sessionId.value)?.hasResults : false
  const hasNotebookEntries = documentStore.documents.length > 0
  const canAccessAnalyzeData = hasCollectionData || hasNotebookEntries

  // Check if we have review data (approved/rejected articles)
  const hasReviewData = dataReviewStore.reviewData != null
  const totalItems = dataReviewStore.reviewData?.overview?.totalItems || 0
  const canAccessAnalyzeRisk = hasReviewData && totalItems > 0

  // Check if we have risk analysis
  const hasRiskAnalysis = riskAnalysisStore.hasRiskAnalysis

  // Base requirements for each phase
  const requirements = {
    organize: true, // Always available
    find: true, // Always available
    research: hasEntity, // Need entity selected
    analyzeData: canAccessAnalyzeData, // Need collection data or notebook
    analyzeRisk: canAccessAnalyzeRisk, // Need review data
    report: hasRiskAnalysis // Need risk analysis complete
  }

  // Get the index of the current phase
  const currentPhaseIndex = phases.findIndex(p => p.key === currentPhase.value)

  // Unlock all phases up to and including the current phase
  // This allows navigation back to any previous phase
  const availability = {}
  phases.forEach((phase, index) => {
    if (index <= currentPhaseIndex) {
      // All phases up to current are unlocked
      availability[phase.key] = true
    } else {
      // Future phases require their prerequisites
      availability[phase.key] = requirements[phase.key]
    }
  })

  return availability
})

// Check if user can navigate to a phase
const canNavigateToPhase = (phaseKey) => {
  return phaseAvailability.value[phaseKey] || false
}

// Check if a phase is completed (user has progressed past it)
const isPhaseCompleted = (phaseKey) => {
  const currentPhaseIndex = phases.findIndex(p => p.key === currentPhase.value)
  const phaseIndex = phases.findIndex(p => p.key === phaseKey)
  return phaseIndex < currentPhaseIndex
}

// Get tooltip message for phases
const getPhaseTooltip = (phase) => {
  if (currentPhase.value === phase.key) {
    return `Current: ${phase.label}`
  }
  
  if (!canNavigateToPhase(phase.key)) {
    const requirements = {
      organize: 'Always available',
      find: 'Always available',
      research: sessionId.value ? 'Complete entity selection first' : 'Select a session to continue',
      analyzeData: 'Complete data collection or add notebook entries',
      analyzeRisk: 'Review and approve data first',
      report: 'Complete risk analysis first'
    }
    return requirements[phase.key] || 'Complete previous phase to unlock'
  }
  
  return `Navigate to ${phase.label}`
}

// Navigate to phase
const navigateToPhase = (phase) => {
  if (!canNavigateToPhase(phase.key)) return
  
  const query = {}
  
  // Add projectId and sessionId for phases that need them
  if (['find', 'research', 'analyzeData', 'analyzeRisk', 'report'].includes(phase.key)) {
    if (projectId.value) query.projectId = projectId.value
    if (sessionId.value) query.sessionId = sessionId.value
  }
  
  router.push({
    name: phase.route,
    query
  })
}
</script>

<style scoped>
/* Smooth transitions for phase buttons */
button {
  transition: all 0.2s ease-in-out;
}
</style>
